import { Container, Graphics, Text } from "pixi.js";
import { Persistence } from "../../../config/constants";
import { BaseEntity } from "../../../core/entity/BaseEntity";
import { Entity } from "../../../core/entity/Entity";
import { on } from "../../../core/entity/handler";
import { Game } from "../../../core/Game";
import {
  BenchGroup,
  BenchGroupMember,
} from "../../../game/bench-work/bench-group";
import { pieceSize, WorkSurfaceSize } from "../../../game/bench-work/workpiece";
import { MaterialInstance } from "../../../game/Materials";
import { HAND_CAPACITY, handSpaceLeft } from "../../../game/Person";
import { Player } from "../../../sim/entities/Player";
import { TOOL_SPRITE_SIZE } from "../../../views/material-sprites/toolItem";
import { PIXELS_PER_INCH } from "../../../views/shop-scale";
import { ShellStore } from "../../ShellStore";
import { BenchArrangeView, DragSource } from "./BenchArrangeView";
import { BenchDive } from "./BenchDive";
import { benchStage } from "./benchStage";
import { PieceMotion } from "./PieceMotion";
import { StageFit, stepTurnSpring } from "./stageMath";
import {
  landingSlot,
  pointInRect,
  rectCenter,
  tileAngleDeg,
  tileAt,
  tileScale,
  TOOL_TILE_PAD,
  TRAY_DROP_REACH,
  TRAY_REACH,
  TrayKind,
  TrayLayout,
  trayLayout,
  trayZone,
} from "./trayMath";

/**
 * The two trays along the bottom of the bench view — everything a piece
 * can rest on that isn't the top: the shelf under the bench (every
 * table in the run's `storedMaterials`, pooled the way the rail pools
 * the run's tools) on the left, and the arms (`player.inventory`) on
 * the right. Drawn on the canvas under the stage's frame, so a carried
 * piece passes over them, and the pieces on them are the real material
 * sprites shrunk to tiles on the same kind of holder (`PieceMotion`)
 * they ride on the bench — which is what lets a piece cross between
 * the top and a tray as one sprite scaling, never a swap.
 *
 * At rest a tray is compact: its label, its count, and its tiles lapped
 * over one another. Hovered with nothing in hand, it fans open into one
 * slot per space it has, empty slots included. While something is up —
 * a carried piece, or the tool in hand — a tray that will take it fans
 * open the same way as the hand comes near (`TRAY_DROP_REACH`), with the
 * slot the piece would land in lit (`landingSlot`), and a tray that
 * won't (full) dims. A piece lifted off a tray leaves its slot as a gap
 * until it is set down somewhere else — the sim still has it there, and
 * the gap is where it goes back. The drag itself belongs to
 * `BenchArrangeView` (the bare hands); this view answers what is under
 * a point and draws where everything at rest lies.
 *
 * The geometry is `trayMath` (pure, unit-tested). Reduced motion snaps
 * the fan and, through the holders, the tiles.
 */

/** Where a tray tile lives in the sim. */
export type TrayPlace =
  | { readonly kind: "shelf"; readonly member: BenchGroupMember }
  | { readonly kind: "arms" };

/** A tile the hand can take off a tray, and where it is on screen. */
export interface TrayTile {
  readonly material: MaterialInstance;
  readonly place: TrayPlace;
  readonly x: number;
  readonly y: number;
  readonly scale: number;
  readonly angleDeg: number;
}

/**
 * A piece's footprint for sizing it as a tile. Stock is its placed size;
 * a tool's sprite is drawn well inside the nominal square `pieceSize`
 * gives it (`createToolItemSprite`), so it is measured by what is
 * actually drawn — otherwise a tool tile shrinks to a speck.
 */
export function tileFootprint(material: MaterialInstance): WorkSurfaceSize {
  if (material.type === "tool") {
    const inches = TOOL_SPRITE_SIZE / PIXELS_PER_INCH;
    return { widthIn: inches, heightIn: inches };
  }
  return pieceSize(material);
}

/** How much room a piece keeps around it in a slot. */
export function tilePad(material: MaterialInstance): number | undefined {
  return material.type === "tool" ? TOOL_TILE_PAD : undefined;
}

const LABELS: Record<TrayKind, string> = {
  shelf: "UNDER THE BENCH",
  arms: "IN HAND",
};

/** The trays' inset from the window's edges. */
const TRAY_INSET = 20;
const LEDGE_COLOR = 0x0b0907;
const CHROME = 0xf1e6cf;
const HOT = 0xf3c96b;
/** How fast the trays fade in once the lean-in lands (per second). */
const FADE_RATE = 10;

/** Motion the player has asked not to see is pinned, not played. */
function reducedMotion(): boolean {
  return (
    globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false
  );
}

interface TrayEntry {
  readonly material: MaterialInstance;
  readonly place: TrayPlace;
}

/** How a tray reads under the hand this frame. */
interface TrayState {
  /** The bare hand is over it. */
  readonly hovered: boolean;
  /** Something is up: a carried piece, or the tool in hand. */
  readonly dragging: boolean;
  /** It would take what is up. */
  readonly accepts: boolean;
  /** What is up would land here if let go now. */
  readonly hot: boolean;
  /** The slot it would land in, while hot. */
  readonly landing: number | null;
  readonly litId: string | null;
}

/** One tray: its ledge, its label, and the tiles on it. */
class Tray {
  readonly root = new Container();
  private readonly ledge = new Graphics();
  private readonly tiles = new Container();
  private readonly label: Text;
  private openness = 0;
  private velocity = 0;
  private entries: TrayEntry[] = [];
  private capacity = 0;
  private holders = new Map<string, PieceMotion>();
  /** The piece the hand has up off this tray: still on it in the sim,
   * but its slot shows as the gap it goes back into. */
  private lifted: string | null = null;
  private layout: TrayLayout | null = null;
  private fit: StageFit | null = null;
  private syncedKey: string | null = null;
  /** Where a piece about to appear on this tray is coming from — the
   * hand let it go there without a canvas holder to hand over (a tool,
   * drawn as chrome while carried). Its fresh holder starts there. */
  private arrivals = new Map<string, { x: number; y: number }>();

  constructor(
    readonly kind: TrayKind,
    private readonly rimLayer: Container,
  ) {
    this.label = new Text({
      text: LABELS[kind],
      resolution: 2,
      style: {
        fontFamily: "Barlow Condensed",
        fontWeight: "500",
        fontSize: 12,
        letterSpacing: 1.6,
        fill: CHROME,
      },
    });
    this.label.alpha = 0.85;
    this.label.anchor.set(kind === "shelf" ? 0 : 1, 0.5);
    this.root.addChild(this.ledge, this.label, this.tiles);
  }

  count(): number {
    return this.entries.length;
  }

  room(): number {
    return this.capacity - this.entries.length;
  }

  /** The gap's slot, while a piece is up off this tray. */
  liftedIndex(): number | null {
    const index = this.entries.findIndex((e) => e.material.id === this.lifted);
    return index === -1 ? null : index;
  }

  /** Each tile's table, in slot order — how the pooled shelf is laid. */
  tableOrders(group: BenchGroup): number[] {
    return this.entries.map(({ place }) =>
      place.kind === "shelf"
        ? group.members.findIndex((m) => m.key === place.member.key)
        : 0,
    );
  }

  /** The ledge's middle on screen, for the chrome and the specs. */
  center(): { x: number; y: number } | null {
    return this.layout ? rectCenter(this.layout.ledge) : null;
  }

  tilePoints(): ReadonlyArray<{
    id: string;
    x: number;
    y: number;
    /** Which tool, for a tool's tile — the chrome marks these for the
     * tutorial to point at. */
    toolId?: string;
  }> {
    if (!this.layout) return [];
    return this.entries.flatMap((entry, index) => {
      if (entry.material.id === this.lifted) return [];
      const { x, y } = rectCenter(this.layout!.slots[index]);
      return {
        id: entry.material.id,
        x,
        y,
        toolId:
          entry.material.type === "tool" ? entry.material.toolId : undefined,
      };
    });
  }

  expectArrival(id: string, x: number, y: number): void {
    this.arrivals.set(id, { x, y });
  }

  /** Whether a point is over the tray, reaching `reach` past its ledge. */
  over(x: number, y: number, reach: number): boolean {
    return (
      this.layout !== null && pointInRect(trayZone(this.layout, reach), x, y)
    );
  }

  tileUnder(x: number, y: number): TrayTile | null {
    if (!this.layout || !this.fit) return null;
    const index = tileAt(this.layout, this.entries.length, x, y);
    if (index === null) return null;
    const entry = this.entries[index];
    // The gap a lifted piece left has nothing in it to take.
    if (entry.material.id === this.lifted) return null;
    const center = rectCenter(this.layout.slots[index]);
    const size = tileFootprint(entry.material);
    return {
      material: entry.material,
      place: entry.place,
      x: center.x,
      y: center.y,
      scale: tileScale(size, this.fit.pxPerIn, tilePad(entry.material)),
      angleDeg: tileAngleDeg(size),
    };
  }

  /**
   * Bring the tray up to date with the sim: what lies on it, how much
   * room it has, and where each tile's holder should be. Holders are
   * kept across syncs so a tile that moves slots springs there; a tile
   * arriving from the hand adopts the holder that rode it. The piece the
   * hand has up (`lifted`) keeps its slot but gets no holder — the hand
   * draws it.
   */
  sync(
    entries: TrayEntry[],
    capacity: number,
    fit: StageFit,
    anchor: { edgeX: number; bottomY: number },
    arrange: BenchArrangeView | undefined,
    lifted: string | null,
  ): void {
    this.entries = entries;
    this.capacity = capacity;
    this.fit = fit;
    this.lifted = lifted;
    this.label.text = `${LABELS[this.kind]} · ${entries.length}/${capacity}`;
    const seen = new Set<string>();
    for (const entry of entries) {
      if (entry.material.id === lifted) continue;
      seen.add(entry.material.id);
      if (this.holders.has(entry.material.id)) continue;
      const handed = arrange?.takeHandoff(entry.material.id) ?? null;
      const motion = handed ?? new PieceMotion(this.rimLayer);
      if (!handed) {
        // A fresh holder is a tile from its first frame — never a full
        // size piece shrinking into place — and one the hand announced
        // starts where the hand let it go, then springs into its slot.
        const size = tileFootprint(entry.material);
        motion.setTileScale(
          tileScale(size, fit.pxPerIn, tilePad(entry.material)),
          { snap: true },
        );
        const arrival = this.arrivals.get(entry.material.id);
        if (arrival) {
          motion.arriveFrom(
            {
              xIn: (arrival.x - fit.originX) / fit.pxPerIn,
              yIn: (arrival.y - fit.originY) / fit.pxPerIn,
              angleDeg: tileAngleDeg(size),
              flipped: false,
            },
            { lifted: true },
          );
        }
      }
      motion.setCarried(false);
      this.holders.set(entry.material.id, motion);
      this.tiles.addChild(motion.holder);
    }
    this.arrivals.clear();
    for (const [id, motion] of this.holders) {
      if (seen.has(id)) continue;
      this.tiles.removeChild(motion.holder);
      motion.destroy();
      this.holders.delete(id);
    }
    entries.forEach((entry, index) => {
      const motion = this.holders.get(entry.material.id);
      if (motion) this.tiles.setChildIndex(motion.holder, index);
    });
    this.place(anchor);
  }

  /** The layout at the current fan, and the tiles retargeted onto it. */
  private place(anchor: { edgeX: number; bottomY: number }): void {
    const fit = this.fit;
    if (!fit) return;
    this.layout = trayLayout(
      { kind: this.kind, edgeX: anchor.edgeX, bottomY: anchor.bottomY },
      {
        capacity: this.capacity,
        count: this.entries.length,
        openness: this.openness,
        labelWidth: this.label.width,
      },
    );
    this.label.position.set(this.layout.labelX, this.layout.labelY);
    this.entries.forEach((entry, index) => {
      const motion = this.holders.get(entry.material.id);
      const slot = this.layout!.slots[index];
      if (!motion || !slot) return;
      const size = tileFootprint(entry.material);
      const center = rectCenter(slot);
      motion.setTileScale(
        tileScale(size, fit.pxPerIn, tilePad(entry.material)),
      );
      motion.retarget(
        entry.material,
        {
          xIn: (center.x - fit.originX) / fit.pxPerIn,
          yIn: (center.y - fit.originY) / fit.pxPerIn,
          angleDeg: tileAngleDeg(size),
          flipped: false,
        },
        fit,
      );
    });
  }

  /**
   * One frame: the fan springs toward open under a bare hand, or under
   * whatever is up once the tray is where it would land; the tiles
   * settle on their holders, and the ledge is drawn for the state the
   * hand has it in.
   */
  step(
    dt: number,
    anchor: { edgeX: number; bottomY: number },
    state: TrayState,
  ): void {
    const open = state.dragging ? state.hot : state.hovered;
    const target = open ? 1 : 0;
    if (reducedMotion()) {
      this.openness = target;
      this.velocity = 0;
    } else if (this.openness !== target) {
      [this.openness, this.velocity] = stepTurnSpring(
        this.openness,
        this.velocity,
        target,
        dt,
      );
    }
    this.place(anchor);
    for (const [id, motion] of this.holders) {
      motion.setLit(id === state.litId);
      motion.step(dt);
    }
    this.draw(state);
  }

  private draw(state: TrayState): void {
    const g = this.ledge;
    g.clear();
    const layout = this.layout;
    if (!layout) return;
    const { x, y, width, height } = layout.ledge;
    const armed = state.dragging && state.accepts;
    const refusing = state.dragging && !state.accepts;
    this.root.alpha = refusing ? 0.4 : 1;
    g.roundRect(x, y, width, height, 8).fill({
      color: LEDGE_COLOR,
      alpha: armed ? 0.78 : 0.62,
    });
    if (state.hot) {
      g.roundRect(x, y, width, height, 8).fill({
        color: 0xffffff,
        alpha: 0.08,
      });
    }
    g.roundRect(x, y, width, height, 8).stroke({
      color: state.hot ? HOT : CHROME,
      alpha: state.hot ? 1 : armed ? 0.6 : 0.18,
      width: state.hot ? 2.5 : armed ? 2 : 1,
    });
    // Empty slots show as the tray opens: the room it has, in outline —
    // the gap a lifted piece left among them. The slot what is up would
    // land in is lit the moment the tray is its target, and brightens
    // as it fans into place.
    const empties = this.openness;
    const landing = state.hot ? state.landing : null;
    layout.slots.forEach((slot, index) => {
      const filled =
        index < this.entries.length &&
        this.entries[index].material.id !== this.lifted;
      if (index === landing) {
        const showing = 0.4 + 0.6 * empties;
        g.roundRect(slot.x, slot.y, slot.width, slot.height, 5)
          .fill({ color: HOT, alpha: 0.16 * showing })
          .stroke({ color: HOT, alpha: showing, width: 2 });
        return;
      }
      if (filled || empties <= 0.001) return;
      g.roundRect(slot.x, slot.y, slot.width, slot.height, 5)
        .fill({ color: 0xffffff, alpha: 0.04 * empties })
        .stroke({ color: CHROME, alpha: 0.28 * empties, width: 1 });
    });
  }

  clear(): void {
    for (const motion of this.holders.values()) {
      this.tiles.removeChild(motion.holder);
      motion.destroy();
    }
    this.holders.clear();
    this.entries = [];
    this.lifted = null;
    this.layout = null;
    this.syncedKey = null;
    this.ledge.clear();
  }

  /** Whether the sim, the fit, or the window moved since the last sync. */
  stale(key: string): boolean {
    if (key === this.syncedKey) return false;
    this.syncedKey = key;
    return true;
  }
}

export class BenchTrayView extends BaseEntity implements Entity {
  persistenceLevel: number = Persistence.Permanent;
  pausable = false;

  private readonly shelf: Tray;
  private readonly arms: Tray;
  private hovered: TrayKind | null = null;
  /** The tray fanned open under whatever is up, and the slot lit in it. */
  private landing: { tray: TrayKind; index: number } | null = null;

  /**
   * @param layer the container the trays draw in — the dive view keeps
   * one under its frame, so the carried piece passes over the ledges.
   * @param rimLayer where a lit tile's rim goes (the dive's x-ray layer).
   */
  constructor(
    private readonly layer: Container,
    rimLayer: Container,
  ) {
    super();
    this.shelf = new Tray("shelf", rimLayer);
    this.arms = new Tray("arms", rimLayer);
    this.layer.addChild(this.shelf.root, this.arms.root);
    this.layer.alpha = 0;
  }

  onDestroy() {
    this.shelf.clear();
    this.arms.clear();
    this.layer.removeChildren();
  }

  private tray(kind: TrayKind): Tray {
    return kind === "shelf" ? this.shelf : this.arms;
  }

  /** The tray a point is over: the ledge and a little past it under
   * the bare hand, further while something is up, so a tray opens as a
   * piece comes near and the open ledge keeps it under the hand. */
  trayUnder(x: number, y: number): TrayKind | null {
    const reach = this.somethingUp() ? TRAY_DROP_REACH : TRAY_REACH;
    if (this.shelf.root.visible && this.shelf.over(x, y, reach)) return "shelf";
    if (this.arms.root.visible && this.arms.over(x, y, reach)) return "arms";
    return null;
  }

  /** Whether the hand has anything up — a carried piece, or the tool
   * in hand — that a tray could take. */
  private somethingUp(): boolean {
    const arrange = this.game.entities.tryGetSingleton(BenchArrangeView);
    return (
      arrange?.draggingId() != null || (arrange?.carrying() ?? null) !== null
    );
  }

  /** The tile under a point, on either tray. */
  tileUnder(x: number, y: number): TrayTile | null {
    return (
      (this.shelf.root.visible ? this.shelf.tileUnder(x, y) : null) ??
      (this.arms.root.visible ? this.arms.tileUnder(x, y) : null)
    );
  }

  /** A piece the hand is about to put on a tray with no holder to hand
   * over: its tile starts where the hand let it go. */
  expectArrival(kind: TrayKind, id: string, x: number, y: number): void {
    this.tray(kind).expectArrival(id, x, y);
  }

  /**
   * Whether a tray would take a piece let go (or a tool in hand set
   * down) over it: room, unless the piece came off that very tray —
   * letting it go where it was picked up is always allowed, it just
   * goes back.
   */
  accepts(kind: TrayKind, fromKind: DragSource["kind"]): boolean {
    if (fromKind === kind) return true;
    const player = this.game.entities.tryGetSingleton(Player);
    const handRoom = player !== undefined && handSpaceLeft(player) > 0;
    if (kind === "arms") return handRoom;
    // A tool off the rail comes down into the arms on its way to the
    // shelf, so both need room.
    if (fromKind === "hook") return handRoom && this.shelf.room() > 0;
    return this.shelf.room() > 0;
  }

  /** How big a piece is as one of this tray's tiles. */
  tileScaleFor(material: MaterialInstance, fit: StageFit): number {
    return tileScale(tileFootprint(material), fit.pxPerIn, tilePad(material));
  }

  /** Where each tray's ledge sits, for the chrome. */
  ledgeCenter(kind: TrayKind): { x: number; y: number } | null {
    const tray = this.tray(kind);
    return tray.root.visible ? tray.center() : null;
  }

  /** Every tile's middle on screen — a spec drags them by name. */
  tilePoints(): ReadonlyArray<{
    tray: TrayKind;
    id: string;
    x: number;
    y: number;
    toolId?: string;
  }> {
    return [
      ...this.shelf.tilePoints().map((p) => ({ tray: "shelf" as const, ...p })),
      ...this.arms.tilePoints().map((p) => ({ tray: "arms" as const, ...p })),
    ];
  }

  @on("render")
  onRender(dt: number) {
    const game = this.game;
    const dive = game.entities.tryGetSingleton(BenchDive);
    const stage = benchStage(game);
    const renderer = game.renderer;
    if (!dive || !stage || !renderer || dive.openBenchKey === null) {
      this.layer.alpha = 0;
      this.shelf.clear();
      this.arms.clear();
      this.hovered = null;
      return;
    }
    // The trays belong to the settled view: they come up once the
    // lean-in lands and go the moment the player stands.
    const settled = dive.settled();
    const targetAlpha = settled ? 1 : 0;
    this.layer.alpha = reducedMotion()
      ? targetAlpha
      : this.layer.alpha +
        (targetAlpha - this.layer.alpha) * Math.min(1, dt * FADE_RATE);

    const { fit, group } = stage;
    const width = renderer.getWidth();
    const height = renderer.getHeight();
    const bottomY = height - TRAY_INSET;
    const shelfAnchor = { edgeX: TRAY_INSET, bottomY };
    const armsAnchor = { edgeX: width - TRAY_INSET, bottomY };
    const store = game.entities.tryGetSingleton(ShellStore);
    const arrange = game.entities.tryGetSingleton(BenchArrangeView);
    // A piece riding the hand is on no tray while it's up — carried by
    // a press, or a tool held as the mode (still on its tray in the sim,
    // lifted only in the picture).
    const dragging = arrange?.draggingId() ?? dive.heldItemId();
    const player = game.entities.tryGetSingleton(Player);

    // Sync with the sim when it moved (or the window did).
    const key = `${dive.openBenchKey}|${store?.version ?? 0}|${dragging ?? ""}|${fit.pxPerIn},${fit.originX},${fit.originY}|${width}x${height}`;
    if (this.shelf.stale(key)) {
      this.shelf.sync(
        shelfEntries(group),
        group.members.reduce(
          (sum, member) => sum + member.machine.materialStorage,
          0,
        ),
        fit,
        shelfAnchor,
        arrange,
        dragging,
      );
      this.shelf.root.visible = group.members.some(
        (member) => member.machine.materialStorage > 0,
      );
    }
    if (this.arms.stale(key)) {
      this.arms.sync(
        (player?.inventory ?? []).map((material) => ({
          material,
          place: { kind: "arms" },
        })),
        HAND_CAPACITY,
        fit,
        armsAnchor,
        arrange,
        dragging,
      );
      this.arms.root.visible = player !== undefined;
    }

    // A bare hand over a tray opens it; the hand carrying something
    // never does, it only lights the tray as a place to let go.
    const [px, py] = game.io.mousePosition;
    const overTray =
      settled && !dive.handsFull() ? this.trayUnder(px, py) : null;
    if (overTray !== this.hovered) {
      this.hovered = overTray;
      store?.bump();
    }
    // The trays read as places to let go while anything is up: a
    // carried piece, or the tool in hand (which a click sets down). The
    // one it would land on fans open with the landing slot lit.
    const dropTarget = arrange?.dropTargetKind() ?? null;
    const source = arrange?.dragSource()?.kind ?? arrange?.carrying()?.fromKind;
    const litId = arrange?.handPieceId() ?? null;
    const landing =
      dropTarget === "shelf" || dropTarget === "arms"
        ? this.landingOn(dropTarget, source, group, arrange)
        : null;
    if (
      landing?.tray !== this.landing?.tray ||
      landing?.index !== this.landing?.index
    ) {
      this.landing = landing;
      store?.bump();
    }
    for (const tray of [this.shelf, this.arms]) {
      if (!tray.root.visible) continue;
      tray.step(dt, tray.kind === "shelf" ? shelfAnchor : armsAnchor, {
        hovered: this.hovered === tray.kind,
        dragging: source !== undefined,
        accepts: source !== undefined && this.accepts(tray.kind, source),
        hot: dropTarget === tray.kind,
        landing: landing?.tray === tray.kind ? landing.index : null,
        litId,
      });
    }
  }

  /**
   * The slot what is up would land in on this tray: back into its own
   * gap if it came off this tray, else where the sim would put it — the
   * arms on the end, the shelf among the tiles of the table whose shelf
   * takes it (`BenchArrangeView.shelfLanding`).
   */
  private landingOn(
    kind: TrayKind,
    source: DragSource["kind"] | undefined,
    group: BenchGroup,
    arrange: BenchArrangeView | undefined,
  ): { tray: TrayKind; index: number } | null {
    const tray = this.tray(kind);
    const lifted = source === kind ? tray.liftedIndex() : null;
    let onto = 0;
    if (kind === "shelf" && lifted === null) {
      const member = arrange?.shelfLanding() ?? null;
      if (!member) return null;
      onto = group.members.findIndex((m) => m.key === member.key);
    }
    return {
      tray: kind,
      index: landingSlot(tray.tableOrders(group), onto, lifted),
    };
  }

  /** Which tray is fanned open, for the chrome: under the bare hand, or
   * under whatever is up as the place it would land. */
  openTray(): TrayKind | null {
    return this.hovered ?? this.landing?.tray ?? null;
  }

  /** The slot lit as where what is up would land, for the chrome. */
  dropSlot(): { tray: TrayKind; index: number } | null {
    return this.landing;
  }
}

/**
 * The tray the pointer is over, if any. The trays are chrome along the
 * bottom of the view: a press over one belongs to the tray — a tile
 * picked up, or the tool in hand set down — even where a piece hangs
 * off the bench far enough to lie under it on screen (a pallet on the
 * makeshift bench overhangs the whole band), so the work views stand
 * down there.
 */
export function trayUnderPointer(game: Game): TrayKind | null {
  const trays = game.entities.tryGetSingleton(BenchTrayView);
  if (!trays) return null;
  const [px, py] = game.io.mousePosition;
  return trays.trayUnder(px, py);
}

/** Everything on every shelf in the run, in table order. */
function shelfEntries(group: BenchGroup): TrayEntry[] {
  return group.members.flatMap((member) =>
    member.machine.storedMaterials.map((material) => ({
      material,
      place: { kind: "shelf" as const, member },
    })),
  );
}
